/**
 * `usePreviewModule` — the live-preview "brain" extracted out of
 * `Workspace.tsx` (design doc `live-preview-side-by-side.md` §4 PR A).
 *
 * This is a REAL hook, not a thin prop-forwarder: it OWNS the
 * `usePreviewSession()` session, the preview construction bridge, the
 * install-state query, the lifecycle handlers (stop / reload / restart /
 * reset / retry), all the derived state (controller policy, dev-loop
 * state, diagnostics, display URL, paired-preview activity, launch-button
 * loading, disabled reason, available title), and the auto-start /
 * cloud-session-reset / diagnostics-refetch effects that used to live
 * inline in the Workspace body. The preview surface is opened manually,
 * via the header "Develop" / "Preview" tabs — there is no auto-open effect
 * (removed; see `docs/plans/2026-08-14-001-feat-design-wizard-preview-timing-fix-plan.md`).
 *
 * BEHAVIOUR IS PRESERVED EXACTLY. Every branch, dependency array, ref, and
 * effect below is a verbatim lift from `Workspace.tsx`; the Workspace-owned
 * inputs the brain reads (`gameId`, `flags`, `interviewState`, run-state,
 * `isAiTyping`, the workspace-mode / split-open setters, …) are passed in as
 * `UsePreviewModuleInput`, and the workspace consumes the returned
 * `UsePreviewModule` object in place of the ~64 inline `previewSession.*` /
 * derived reads. This does NOT change the render-tree position of the
 * preview surface (that stays in `Workspace`), so the no-remount and
 * flag-OFF parity invariants are untouched.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { BuildGameInterviewState } from '@volley/crucible-ipc-types/build-game-types';
import {
  PREVIEW_PHASE_GATE_ERROR_CODE,
  PREVIEW_PHASE_GATE_MIN,
} from '@volley/crucible-ipc-types/preview-gate-constants';
import type {
  FoundryGameStateSnapshot,
  FoundryPreviewDevState,
} from '@volley/crucible-build-state/state-kernel.js';
import type { InstallRetryResult } from '@volley/crucible-ipc-types/ipc-channels';
import type { PreviewPhaseGate } from '../components/PreviewLaunchButton';
import type { PreviewDevLoopState } from '../workspace/preview-state';
import type { PreviewControllerPolicy } from '../workspace/preview-controller-policy';
import type { WorkspaceRunState } from '../workspace/run-state';
import {
  applyPreviewPhoneCapOverride,
  derivePreviewControllerPolicy,
} from '../workspace/preview-controller-policy';
import { derivePreviewDevLoopState } from '../workspace/preview-state';
import {
  mergePreviewDisabledReason,
  previewAvailableTitleForDevLoop,
  previewHandoffDisabledReasonForHandoff,
} from '../workspace/plan-build-handoff';
import {
  isCloudPreviewSession,
  isPairedPreviewActive,
  previewDisplayUrl,
  previewPhoneControlsEnabled,
  usePreviewSession,
  type PreviewSession,
  type PreviewSessionState,
  type RestartResult,
} from '../hooks/usePreviewSession';
import { usePreviewConstructionBridge } from '../hooks/usePreviewConstructionBridge';
import { useInstallStateForGame } from '../hooks/useInstallStateForGame';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';
import { useIpcQuery } from '../hooks/useIpc';

export interface PreviewToast {
  tone: 'info' | 'success' | 'error';
  message: string;
}

/**
 * Workspace-owned inputs the preview brain reads. These stay owned by the
 * Workspace (interview / run-state / flags / layout setters) and are
 * threaded in so the brain can move without pulling the whole Workspace
 * with it.
 */
export interface UsePreviewModuleInput {
  gameId: string | undefined;
  /** Local install sentinel is absent from the Cloud runtime. */
  installStateAvailable: boolean;
  /** `useFeatureFlags()` snapshot — the brain reads `loaded`, `livePreview`, `newUi`, `inAppPairing`. */
  flags: {
    loaded: boolean;
    livePreview: boolean;
    newUi: boolean;
    inAppPairing: boolean;
  };
  interviewState: BuildGameInterviewState | null | undefined;
  foundryGameState: FoundryGameStateSnapshot | null | undefined;
  workspaceRunState: WorkspaceRunState;
  workspaceConfig:
    | {
        previewPhoneCapOverride?: number | null;
      }
    | null
    | undefined;
  /** `chat.isAiTyping` — build-turn-active signal for the construction bridge. */
  isAiTyping: boolean;
  /** Derived `previewPhaseGate` (Workspace-owned useMemo over foundry snapshot + phaseProgress). */
  previewPhaseGate: PreviewPhaseGate | undefined;
  /**
   * True once the current game's authoritative phase projection yields a
   * start decision. This is distinct from `previewPhaseGate === undefined`:
   * legacy games are resolved but intentionally ungated, while hydration and
   * projection errors must not trigger an automatic launch.
   */
  previewPhaseAutoStartReady: boolean;
  /**
   * Stable key for the authoritative phase decision behind auto-start. It
   * advances with gate-relevant evidence (not cloud liveness or same-phase
   * progress), so an expected main-side refusal waits for a genuinely new
   * decision instead of retrying the same stale positive projection.
   */
  previewPhaseAutoStartEvidence: string;
  /** Workspace-owned `workspaceMode` setter (the "Develop" tab flips to chat). */
  setWorkspaceMode: (mode: 'preview' | 'chat' | 'plan' | 'design' | 'protohub') => void;
  openPreviewSurface: () => void;
  setLivePreviewSplitOpenForGame: (open: boolean) => void;
  /** Toast setter — restart / install failures surface here. */
  setPreviewToast: (toast: PreviewToast | null) => void;
}

/**
 * The typed surface the Workspace consumes. Grouped so the ~64 former
 * inline reads become `preview.<group>.<field>`. `session` is the raw
 * `usePreviewSession()` handle (state reads + phone controls); `handlers`
 * are the lifecycle callbacks; `derived` is the computed state; `bridge`
 * are the construction-bridge inputs the display iframe needs.
 */
export interface UsePreviewModule {
  /** Raw preview session handle (state, controllerUrls, reloadKey, add/remove phone, reset). */
  session: PreviewSession;
  /** Convenience alias for `session.state` (the discriminated status union). */
  state: PreviewSessionState;
  handlers: {
    reload: () => void;
    stop: () => void;
    restart: () => void;
    reset: () => void;
    /** Open the preview surface + fire the primary start CTA (start / retry-start / retry-install). */
    launch: () => void;
    /** Retry the preview from the overlay (open + start). */
    retryFromOverlay: () => void;
    /** Header "Develop" tab — chat mode + open split + start when ready. */
    activateDevelop: () => void;
    /** Start the preview for the current game (reconcile-aware). */
    startForCurrentGame: () => void;
    /** Retry the dependency install then start the preview. */
    retryInstallAndStart: () => void;
  };
  derived: {
    controllerPolicy: PreviewControllerPolicy;
    devLoopState: PreviewDevLoopState;
    launchButtonLoading: boolean;
    diagnostics: FoundryPreviewDevState | null;
    displayUrl: string | undefined;
    pairedPreviewActive: boolean;
    phoneControlsEnabled: boolean;
    disabledReason: string | undefined;
    /** True when the run-state handoff gate forces the preview disabled (distinct from a dev-loop disable). */
    forceDisabled: boolean;
    availableTitle: string;
    /** Pairing-next-launch toggle state + setter (owned here, read by the header). */
    weekendPairingNextLaunch: boolean;
    setWeekendPairingNextLaunch: React.Dispatch<React.SetStateAction<boolean>>;
    inAppPairingAvailable: boolean;
  };
  /** Inputs the display-iframe construction bridge needs (owned here). */
  bridge: {
    displayIframeRef: React.RefObject<HTMLIFrameElement | null>;
    onDisplayIframeLoad: (url: string) => void;
  };
}

function previewSessionGameId(state: PreviewSessionState): string | undefined {
  if (state.status === 'ready') return state.session.gameId;
  if (state.status === 'busy' || state.status === 'error') return state.gameId;
  return undefined;
}

/**
 * Runs the explicit-Stop click's guard and action as one pure step
 * (crucible#1016, extracted for crucible#3819).
 *
 * Args:
 *   status: The current previewSession state's status.
 *   stopPreview: The session's stop() call — invoked only when appropriate.
 *
 * Returns:
 *   Nothing. Calls `stopPreview` when the session is ready to be stopped;
 *   a no-op otherwise (idle / busy / error — nothing to tear down, or a
 *   launch is still in flight).
 *
 * Raises:
 *   Never. `stopPreview`'s own rejection handling is unchanged: the caller
 *   still fires it with `void`, same as before this extraction.
 */
export function stopPreviewIfReady(
  status: PreviewSessionState['status'],
  stopPreview: () => Promise<void>,
): void {
  if (status !== 'ready') return;
  void stopPreview();
}

/** The game-switch effect's latch: the last automatic cleanup it already fired. */
export interface ForeignSessionCleanupAttempt {
  targetGameId: string;
  sessionId: string;
}

export type ForeignSessionCleanupDecision =
  | { action: 'stop-foreign'; sessionId: string }
  | { action: 'reset-error' }
  | { action: 'noop' };

/**
 * Decides how the game-switch effect should react to a preview session
 * left over from a DIFFERENT game (crucible#1016, crucible#3177; extracted
 * for crucible#3819).
 *
 * Args:
 *   state: The current previewSession state.
 *   gameId: The workspace's current route gameId.
 *   priorAttempt: The latch recording the last automatic cleanup this
 *     effect already fired for this (gameId, sessionId) pair, if any.
 *
 * Returns:
 *   `'stop-foreign'` with the foreign session's id when a live session (or
 *   a starting launch's previous session) belongs to a different game and
 *   has not already been auto-cleaned for that pair; `'reset-error'` when
 *   a sessionless error belonging to a different game should be cleared;
 *   `'noop'` otherwise, including when the foreign session's cleanup was
 *   already attempted — stop() intentionally restores live state on
 *   failure for manual retry, and re-firing on that restoration would
 *   otherwise create an unbounded IPC retry loop.
 *
 * Raises:
 *   Never.
 */
export function decideForeignSessionCleanup(
  state: PreviewSessionState,
  gameId: string,
  priorAttempt: ForeignSessionCleanupAttempt | undefined,
): ForeignSessionCleanupDecision {
  const foreignSession =
    state.status === 'ready' && state.session.gameId !== gameId
      ? state.session
      : state.status === 'error' && state.gameId !== gameId && state.operation === 'starting'
        ? state.previousSession
        : undefined;
  if (foreignSession) {
    if (
      priorAttempt?.targetGameId === gameId &&
      priorAttempt.sessionId === foreignSession.sessionId
    ) {
      return { action: 'noop' };
    }
    return { action: 'stop-foreign', sessionId: foreignSession.sessionId };
  }
  if (state.status === 'error' && state.gameId !== gameId) {
    return { action: 'reset-error' };
  }
  return { action: 'noop' };
}

/**
 * Issue #643 (Codex review #1 BLOCK) — the toast handleRestartPreview
 * should surface for a given restart outcome, extracted for crucible#3819.
 *
 * Args:
 *   result: The discriminated result `restart()` resolved with.
 *
 * Returns:
 *   An error toast when the restart failed for a reason other than
 *   'busy'; `null` on success (no toast — the UI reflects the new ready
 *   session) and on 'busy' (a button-mash race the first click already
 *   drives recovery for, so a second toast would be noise).
 *
 * Raises:
 *   Never.
 */
export function restartFailureToast(result: RestartResult): PreviewToast | null {
  if (result.ok) return null;
  if ('reason' in result && result.reason === 'busy') return null;
  if ('error' in result) {
    const errMsg = result.error.message || 'Preview failed to restart';
    const recovery = result.error.recovery
      ? ` ${result.error.recovery}`
      : ' Open Help → Open Logs Folder for details.';
    return { tone: 'error', message: `${errMsg}${recovery}` };
  }
  return null;
}

/**
 * The combined install-retry-then-start recovery CTA's outcome, extracted
 * for crucible#3819. `handleRetryInstallAndStartPreview` runs
 * `installStateForGame.retryInstall()` first and only proceeds to
 * `startPreviewForCurrentGame()` on success — this is the decision that
 * ordering exists to make.
 *
 * Args:
 *   result: The discriminated result `retryInstall()` resolved with.
 *
 * Returns:
 *   `{ action: 'start' }` when the retry succeeded — the caller should
 *   proceed to `startPreviewForCurrentGame()`. Otherwise an error toast
 *   naming the failure reason, and the caller must NOT start preview (a
 *   failed install has nothing to preview yet).
 *
 * Raises:
 *   Never.
 */
export function installRetryDecision(
  result: InstallRetryResult,
): { action: 'start' } | { action: 'toast'; toast: PreviewToast } {
  if (!result.ok) {
    return {
      action: 'toast',
      toast: { tone: 'error', message: `Install failed: ${result.reason}` },
    };
  }
  return { action: 'start' };
}

/**
 * What must be true for the preview diagnostics panel's `game-state:get`
 * cache entry to need invalidating, extracted for crucible#3819. The
 * effect that calls this fires on session/phone-count changes; this
 * function is the GUARD (and the values threaded into the invalidation),
 * not the effect's own re-run trigger — see this function's residual note
 * in the triage record for what it does not prove.
 *
 * Args:
 *   gameId: The workspace's current route gameId, if any.
 *   previewReadySessionId: The ready preview session's id, or `null` when
 *     no session is ready.
 *   previewPhoneCount: The active session's controller URL count.
 *
 * Returns:
 *   `null` when there is no game or no ready session to invalidate
 *   diagnostics for. Otherwise the values the invalidation is keyed on —
 *   proof that `previewPhoneCount` is threaded through, not just gameId
 *   and session id.
 *
 * Raises:
 *   Never.
 */
export function previewGameStateInvalidationTrigger(
  gameId: string | undefined,
  previewReadySessionId: string | null,
  previewPhoneCount: number,
): { gameId: string; previewReadySessionId: string; previewPhoneCount: number } | null {
  if (!gameId || !previewReadySessionId) return null;
  return { gameId, previewReadySessionId, previewPhoneCount };
}

/**
 * The preview brain. See file-level doc — a verbatim lift from Workspace,
 * behaviour-preserving, taking the Workspace-owned inputs above.
 */
export function usePreviewModule(input: UsePreviewModuleInput): UsePreviewModule {
  const {
    gameId,
    installStateAvailable,
    flags,
    interviewState,
    foundryGameState,
    workspaceRunState,
    workspaceConfig,
    isAiTyping,
    previewPhaseGate,
    previewPhaseAutoStartReady,
    previewPhaseAutoStartEvidence,
    setWorkspaceMode,
    openPreviewSurface,
    setLivePreviewSplitOpenForGame,
    setPreviewToast,
  } = input;

  const client = useCrucibleClient();
  const queryClient = useQueryClient();
  const previewSession = usePreviewSession();
  const installStateForGame = useInstallStateForGame(gameId, { enabled: installStateAvailable });
  // Promise continuations must belong to the exact route incarnation that
  // launched them. A gameId comparison alone is vulnerable to A → B → A;
  // an identity token also becomes stale across that ABA navigation.
  const gameRouteToken = useMemo(() => Symbol(gameId), [gameId]);
  const activeGameRouteTokenRef = useRef<symbol | null>(gameRouteToken);
  useLayoutEffect(() => {
    activeGameRouteTokenRef.current = gameRouteToken;
    return () => {
      if (activeGameRouteTokenRef.current === gameRouteToken) {
        activeGameRouteTokenRef.current = null;
      }
    };
  }, [gameRouteToken]);

  const previewSessionOwnerGameId = previewSessionGameId(previewSession.state);
  const previewSessionOwnedByOtherGame =
    previewSessionOwnerGameId !== undefined && previewSessionOwnerGameId !== gameId;
  const previewSessionStateForCurrentGame: PreviewSessionState = previewSessionOwnedByOtherGame
    ? { status: 'idle' }
    : previewSession.state;
  const previewSessionForCurrentGame: PreviewSession = previewSessionOwnedByOtherGame
    ? {
        ...previewSession,
        state: previewSessionStateForCurrentGame,
        controllerUrls: [],
      }
    : previewSession;

  // Auto-start is bounded to two genuine launch failures per game. An
  // expected phase refusal is tracked separately and refunded once, then held
  // until the authoritative phase decision changes.
  const livePreviewAutoStartAttemptsRef = useRef<Map<string, number>>(new Map());
  const previewPhaseEvidenceAtStartRef = useRef<Map<string, string>>(new Map());
  const previewPhaseRefusalRef = useRef<
    Map<string, { error: unknown; evidence: string; observedNonRunnable: boolean }>
  >(new Map());

  // Live-preview (§3.G) PARENT→iframe construction-event bridge — the POST
  // side. The ref is forwarded onto the display `<iframe>` (both layouts);
  // the hook subscribes to `preview:construction-event` and relays each
  // record + the build-active banner signal into the display iframe via
  // `postMessage(msg, displayOrigin)`. Gated on `flags.livePreview` —
  // flag-OFF: no subscription, no postMessage (byte-identical).
  const displayPreviewIframeRef = useRef<HTMLIFrameElement | null>(null);
  const displayPreviewUrl =
    previewSessionStateForCurrentGame.status === 'ready'
      ? previewDisplayUrl(previewSessionStateForCurrentGame.session)
      : undefined;
  const [loadedDisplayPreviewUrl, setLoadedDisplayPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    setLoadedDisplayPreviewUrl(null);
  }, [displayPreviewUrl]);
  const handleDisplayIframeLoad = useCallback(
    (url: string) => {
      if (url === displayPreviewUrl) {
        setLoadedDisplayPreviewUrl(url);
      }
    },
    [displayPreviewUrl],
  );
  usePreviewConstructionBridge({
    enabled: flags.livePreview,
    gameId,
    iframeRef: displayPreviewIframeRef,
    displayUrl: displayPreviewUrl,
    loadedDisplayUrl: loadedDisplayPreviewUrl,
    isAiTyping,
  });

  // `usePreviewSession()` returns a new object literal every render; the
  // individual methods are stable useCallback refs inside the hook, so
  // destructure them for stable useCallback deps downstream.
  const {
    reload: reloadPreview,
    reset: resetPreview,
    restart: restartPreview,
    start: startPreview,
    stop: stopPreview,
  } = previewSession;
  // A phase-gate refusal can be retained in renderer state after a manual
  // click during hydration (or by an auto-start attempt). Do not immediately
  // clear it from the same stale positive projection that triggered the
  // rejected launch: wait until the authoritative subscription emits fresh
  // evidence, then let the normal auto-start path proceed.
  useEffect(() => {
    if (!gameId) return;
    const current = previewSession.state;
    if (
      (current.status === 'ready' && current.session.gameId === gameId) ||
      (current.status === 'error' &&
        current.gameId === gameId &&
        current.error.code !== PREVIEW_PHASE_GATE_ERROR_CODE)
    ) {
      previewPhaseEvidenceAtStartRef.current.delete(gameId);
      previewPhaseRefusalRef.current.delete(gameId);
      return;
    }
    if (
      current.status === 'error' &&
      current.gameId === gameId &&
      current.error.code === PREVIEW_PHASE_GATE_ERROR_CODE
    ) {
      const recorded = previewPhaseRefusalRef.current.get(gameId);
      if (!recorded || recorded.error !== current.error) {
        const evidence =
          previewPhaseEvidenceAtStartRef.current.get(gameId) ?? previewPhaseAutoStartEvidence;
        previewPhaseRefusalRef.current.set(gameId, {
          error: current.error,
          evidence,
          observedNonRunnable: false,
        });

        // The phase preflight is a gate, not a failed launch. Refund the
        // automatic attempt so reaching Phase 4 still has the full retry
        // budget, while the evidence latch below prevents a hot retry loop.
        const attempts = livePreviewAutoStartAttemptsRef.current.get(gameId);
        if (attempts !== undefined && Number.isFinite(attempts) && attempts > 0) {
          livePreviewAutoStartAttemptsRef.current.set(gameId, attempts - 1);
        }
      }
    }

    const refusal = previewPhaseRefusalRef.current.get(gameId);
    if (!refusal) return;
    if (previewPhaseGate && previewPhaseGate.currentPhase < PREVIEW_PHASE_GATE_MIN) {
      if (!refusal.observedNonRunnable) {
        previewPhaseRefusalRef.current.set(gameId, {
          ...refusal,
          observedNonRunnable: true,
        });
      }
      return;
    }
    if (!previewPhaseAutoStartReady) return;
    if (!refusal.observedNonRunnable && previewPhaseAutoStartEvidence === refusal.evidence) return;

    previewPhaseRefusalRef.current.delete(gameId);
    previewPhaseEvidenceAtStartRef.current.delete(gameId);
    if (
      current.status === 'error' &&
      current.gameId === gameId &&
      current.error.code === PREVIEW_PHASE_GATE_ERROR_CODE
    ) {
      resetPreview();
    }
  }, [
    gameId,
    previewPhaseAutoStartEvidence,
    previewPhaseAutoStartReady,
    previewPhaseGate,
    previewSession.state,
    resetPreview,
  ]);

  const previewControllerPolicyRef = useRef(derivePreviewControllerPolicy(null));
  const automaticForeignCleanupAttemptRef = useRef<ForeignSessionCleanupAttempt | undefined>(
    undefined,
  );
  useEffect(() => {
    if (!gameId) return;
    if (automaticForeignCleanupAttemptRef.current?.targetGameId !== gameId) {
      automaticForeignCleanupAttemptRef.current = undefined;
    }

    const decision = decideForeignSessionCleanup(
      previewSession.state,
      gameId,
      automaticForeignCleanupAttemptRef.current,
    );
    if (decision.action === 'stop-foreign') {
      // See decideForeignSessionCleanup's docblock (crucible#1016) for why
      // the latch is needed before firing stopPreview().
      automaticForeignCleanupAttemptRef.current = {
        targetGameId: gameId,
        sessionId: decision.sessionId,
      };
      void stopPreview();
      return;
    }
    if (decision.action === 'reset-error') {
      resetPreview();
    }
  }, [gameId, previewSession.state, resetPreview, stopPreview]);

  const handleReloadPreview = useCallback(() => {
    reloadPreview();
  }, [reloadPreview]);

  const handleStopPreview = useCallback(() => {
    stopPreviewIfReady(previewSession.state.status, stopPreview);
  }, [previewSession.state.status, stopPreview]);

  const handleRestartPreview = useCallback(() => {
    if (!gameId) return;
    // The stopped toolbar's Play control is wired to restart. Keep that UI
    // path under the same ownership boundary as every other launch path: a
    // retained session for another game must be torn down before this game
    // can replace the renderer's only session handle.
    if (previewSessionOwnedByOtherGame) {
      void stopPreview();
      return;
    }
    const routeToken = gameRouteToken;
    void (async () => {
      const result = await restartPreview(gameId, {
        maxControllerSlots: previewControllerPolicyRef.current.maxControllerSlots,
      });
      if (activeGameRouteTokenRef.current !== routeToken) return;
      const toast = restartFailureToast(result);
      if (toast) setPreviewToast(toast);
    })();
  }, [
    gameId,
    gameRouteToken,
    previewSessionOwnedByOtherGame,
    restartPreview,
    setPreviewToast,
    stopPreview,
  ]);

  const previewDevLoopState = useMemo(
    () =>
      derivePreviewDevLoopState({
        gameId,
        phaseGate: previewPhaseGate,
        installState: installStateForGame.data,
        installStateLoading: installStateForGame.isLoading,
        installRetrying: installStateForGame.isRetrying,
        installRetryError: installStateForGame.retryError,
        previewSession: previewSessionStateForCurrentGame,
        foundryPreviewDev: foundryGameState?.previewDev,
      }),
    [
      foundryGameState?.previewDev,
      gameId,
      installStateForGame.data,
      installStateForGame.isLoading,
      installStateForGame.isRetrying,
      installStateForGame.retryError,
      previewPhaseGate,
      previewSessionStateForCurrentGame,
    ],
  );
  const previewLaunchButtonLoading =
    previewDevLoopState.kind === 'checking' ||
    previewDevLoopState.kind === 'installing' ||
    previewDevLoopState.kind === 'starting' ||
    previewDevLoopState.kind === 'restarting' ||
    previewDevLoopState.kind === 'stopping';
  const [weekendPairingNextLaunch, setWeekendPairingNextLaunch] = useState(false);
  const inAppPairingAvailable = flags.loaded && flags.inAppPairing;
  const pairedPreviewActive = isPairedPreviewActive(previewSessionStateForCurrentGame);
  // Multi-controller (cloud + local): +Add / −Remove / per-pane-✕ are wired
  // for any ready, non-paired session (cloud INCLUDED).
  const phoneControlsEnabled = previewPhoneControlsEnabled(previewSessionStateForCurrentGame);
  useEffect(() => {
    if (!inAppPairingAvailable) {
      setWeekendPairingNextLaunch(false);
    }
  }, [inAppPairingAvailable]);

  const startPreviewForCurrentGame = useCallback(() => {
    // Never overwrite the only renderer handle to another game's live
    // session. The route-switch cleanup effect must settle first; otherwise a
    // same-frame manual click could orphan the old processes before dev:stop.
    // If the one automatic cleanup attempt failed, the user's Start click is
    // an explicit bounded retry of that teardown; a successful stop lets
    // auto-start (or the next manual click) launch the current game.
    if (!gameId) return;
    if (previewSessionOwnedByOtherGame) {
      void stopPreview();
      return;
    }
    if (previewSession.state.status === 'busy') {
      return;
    }
    const policy = previewControllerPolicyRef.current;
    // Pod-preview Phase A — a cloud preview is display-only: no controller
    // slots to reconcile, and it's already the live session for this game.
    if (
      previewSession.state.status === 'ready' &&
      previewSession.state.session.gameId === gameId &&
      isCloudPreviewSession(previewSession.state.session)
    ) {
      return;
    }
    if (
      previewSession.state.status === 'ready' &&
      !isCloudPreviewSession(previewSession.state.session) &&
      previewSession.state.session.gameId === gameId &&
      previewSession.state.session.urls.controllers.length === policy.defaultControllerSlots &&
      previewSession.state.session.maxControllerSlots === policy.maxControllerSlots &&
      pairedPreviewActive === (inAppPairingAvailable && weekendPairingNextLaunch)
    ) {
      return;
    }
    previewPhaseEvidenceAtStartRef.current.set(gameId, previewPhaseAutoStartEvidence);
    void startPreview(gameId, {
      phoneCount: policy.defaultControllerSlots,
      maxControllerSlots: policy.maxControllerSlots,
      ...(inAppPairingAvailable && weekendPairingNextLaunch ? { pairing: true } : {}),
    });
  }, [
    gameId,
    inAppPairingAvailable,
    pairedPreviewActive,
    previewPhaseAutoStartEvidence,
    previewSessionOwnedByOtherGame,
    previewSession.state,
    startPreview,
    stopPreview,
    weekendPairingNextLaunch,
  ]);

  const handleRetryInstallAndStartPreview = useCallback(() => {
    if (!gameId || installStateForGame.isRetrying) return;
    openPreviewSurface();
    const routeToken = gameRouteToken;
    void (async () => {
      const result = await installStateForGame.retryInstall();
      if (activeGameRouteTokenRef.current !== routeToken) return;
      const decision = installRetryDecision(result);
      if (decision.action === 'toast') {
        setPreviewToast(decision.toast);
        return;
      }
      startPreviewForCurrentGame();
    })();
  }, [
    gameId,
    gameRouteToken,
    installStateForGame,
    openPreviewSurface,
    setPreviewToast,
    startPreviewForCurrentGame,
  ]);

  const handleLaunchPreview = useCallback(() => {
    openPreviewSurface();
    if (!gameId) return;
    if (previewDevLoopState.primaryCta === 'retry-install-and-start') {
      handleRetryInstallAndStartPreview();
      return;
    }
    if (
      previewDevLoopState.primaryCta === 'start-preview' ||
      previewDevLoopState.primaryCta === 'retry-start-preview'
    ) {
      startPreviewForCurrentGame();
    }
  }, [
    gameId,
    handleRetryInstallAndStartPreview,
    openPreviewSurface,
    previewDevLoopState.primaryCta,
    startPreviewForCurrentGame,
  ]);

  const handleRetryPreviewFromOverlay = useCallback(() => {
    openPreviewSurface();
    startPreviewForCurrentGame();
  }, [openPreviewSurface, startPreviewForCurrentGame]);

  // The "Develop" tab (default, side-by-side). It NEVER closes the split —
  // clicking it from Plan/Design returns to chat mode with the preview docked.
  const handleActivateDevelop = useCallback(() => {
    setWorkspaceMode('chat');
    setLivePreviewSplitOpenForGame(true);
    if (
      previewDevLoopState.primaryCta === 'start-preview' ||
      previewDevLoopState.primaryCta === 'retry-start-preview'
    ) {
      startPreviewForCurrentGame();
    }
  }, [
    previewDevLoopState.primaryCta,
    startPreviewForCurrentGame,
    setLivePreviewSplitOpenForGame,
    setWorkspaceMode,
  ]);

  const previewControllerPolicy = useMemo(
    () =>
      applyPreviewPhoneCapOverride(
        derivePreviewControllerPolicy(interviewState),
        workspaceConfig?.previewPhoneCapOverride,
      ),
    [interviewState, workspaceConfig?.previewPhoneCapOverride],
  );
  previewControllerPolicyRef.current = previewControllerPolicy;

  const previewHandoffDisabledReason = previewHandoffDisabledReasonForHandoff({
    runStateKind: workspaceRunState.kind,
    previewDevLoopKind: previewDevLoopState.kind,
    previewPhaseGate,
  });
  const previewDisabledReason = mergePreviewDisabledReason(
    previewHandoffDisabledReason,
    previewDevLoopState.disabledReason,
  );
  const previewAvailableTitle = previewAvailableTitleForDevLoop(previewDevLoopState);

  // Auto-START the preview session (independent of whether the preview
  // surface is currently visible — starting the dev server is cheap and
  // the user may open the surface at any time via the manual "Develop" /
  // "Preview" tabs). Re-evaluates on every dev-loop state change, with its
  // own per-game latch (attempt-bounded at TWO, pinned to Infinity on
  // first success) so a deliberate user stop is not fought.
  useEffect(() => {
    if (!gameId) return;
    // Codex stop-gate: pin ONLY when the ready session belongs to THIS game.
    if (previewSession.state.status === 'ready' && previewSession.state.session.gameId === gameId) {
      livePreviewAutoStartAttemptsRef.current.set(gameId, Number.POSITIVE_INFINITY);
    }
  }, [gameId, previewSession.state]);
  useEffect(() => {
    if (
      !flags.livePreview ||
      !flags.newUi ||
      !gameId ||
      !previewPhaseAutoStartReady ||
      previewSessionOwnedByOtherGame ||
      previewPhaseRefusalRef.current.has(gameId)
    ) {
      return;
    }
    const attempts = livePreviewAutoStartAttemptsRef.current.get(gameId) ?? 0;
    if (attempts >= 2) return;
    if (
      previewDevLoopState.primaryCta === 'start-preview' ||
      previewDevLoopState.primaryCta === 'retry-start-preview'
    ) {
      livePreviewAutoStartAttemptsRef.current.set(gameId, attempts + 1);
      startPreviewForCurrentGame();
    }
  }, [
    flags.livePreview,
    flags.newUi,
    gameId,
    previewPhaseAutoStartReady,
    previewSessionOwnedByOtherGame,
    previewDevLoopState.primaryCta,
    startPreviewForCurrentGame,
  ]);

  // Diagnostics — refetch `game-state:get` when the ready session or active
  // phone count changes so the diagnostics panel tracks controller URL count
  // and warnings.
  const previewGameStateQueryArgs = useMemo(() => ({ gameId: gameId ?? '' }), [gameId]);
  const previewReadySessionId =
    previewSessionStateForCurrentGame.status === 'ready'
      ? previewSessionStateForCurrentGame.session.sessionId
      : null;
  const { data: previewGameState } = useIpcQuery('game-state:get', previewGameStateQueryArgs, {
    enabled: !!gameId && previewSessionStateForCurrentGame.status === 'ready',
    staleTime: 1_000,
  });
  const previewPhoneCount = previewSessionForCurrentGame.controllerUrls.length;
  useEffect(() => {
    // See previewGameStateInvalidationTrigger's docblock (crucible#3819) for
    // the guard + the values this invalidation is keyed on.
    if (!previewGameStateInvalidationTrigger(gameId, previewReadySessionId, previewPhoneCount)) {
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: projectQueryKey(client, ['game-state:get', previewGameStateQueryArgs]),
    });
  }, [
    client,
    gameId,
    previewGameStateQueryArgs,
    previewReadySessionId,
    previewPhoneCount,
    queryClient,
  ]);
  const previewDiagnostics = previewGameState?.previewDev ?? null;

  return {
    session: previewSessionForCurrentGame,
    state: previewSessionStateForCurrentGame,
    handlers: {
      reload: handleReloadPreview,
      stop: handleStopPreview,
      restart: handleRestartPreview,
      reset: resetPreview,
      launch: handleLaunchPreview,
      retryFromOverlay: handleRetryPreviewFromOverlay,
      activateDevelop: handleActivateDevelop,
      startForCurrentGame: startPreviewForCurrentGame,
      retryInstallAndStart: handleRetryInstallAndStartPreview,
    },
    derived: {
      controllerPolicy: previewControllerPolicy,
      devLoopState: previewDevLoopState,
      launchButtonLoading: previewLaunchButtonLoading,
      diagnostics: previewDiagnostics,
      displayUrl: displayPreviewUrl,
      pairedPreviewActive,
      phoneControlsEnabled,
      disabledReason: previewDisabledReason,
      forceDisabled: previewHandoffDisabledReason !== undefined,
      availableTitle: previewAvailableTitle,
      weekendPairingNextLaunch,
      setWeekendPairingNextLaunch,
      inAppPairingAvailable,
    },
    bridge: {
      displayIframeRef: displayPreviewIframeRef,
      onDisplayIframeLoad: handleDisplayIframeLoad,
    },
  };
}
